import styles from './AccessoryItem.module.scss';

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ContentType, LinkVariant, ProductModule } from 'api/contentful/contentTypes.cloverdotcom';
import { Image } from 'components/Image/Image';
import { ProductModal } from 'components/ProductModal/ProductModal';
import { SproutButton } from 'components/Button/SproutButton';
import { TextV2 } from 'components/TextV2/TextV2';
import { useCMSFilteredByDcsId } from 'hooks/useCMSByFilters';
import naImage from 'images/na.png';
import { stringFormatForTestAttr } from 'utils/StringFormatForTest';
import type { IUpsellModel } from '../models';
import { ProductDetails } from './ProductDetails';
import { ACCESSORY_ITEM_TEST_ID } from './AccessoryItem.const';
import { usePaymentsToggle } from '../OrderSummary/PaymentsToggle/usePaymentsToggle/usePaymentsToggle';
import { useCheckout } from 'components/Checkout/CheckoutProvider';
import { Installments } from '../OrderSummary/types';
import { useCartContext } from 'components/Cart/hooks/useCartContext';

interface IAccessoryItemProps {
  product: IUpsellModel;
}

export const AccessoryItem: React.FC<IAccessoryItemProps> = ({ product }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { t } = useTranslation();
  const { addProduct } = useCartContext();
  const { isPayMonthly } = usePaymentsToggle();
  const { selectedPurchaseType } = useCheckout();

  const subscription36MonthsPricing = product.installmentPlans?.SL36?.value;
  const subscription48MonthsPricing = product.installmentPlans?.SL48?.value;
  const subscriptionCostByPaymentType =
    selectedPurchaseType === Installments.SL36
      ? subscription36MonthsPricing && t('cart-page.monthly-price', { price: subscription36MonthsPricing })
      : subscription48MonthsPricing && t('cart-page.monthly-price', { price: subscription48MonthsPricing });

  const accessoryCostByPaymentType =
    isPayMonthly && subscription36MonthsPricing && subscription48MonthsPricing
      ? subscriptionCostByPaymentType
      : t('cart-page.item-cost', { price: product.totalCost });

  const info = useCMSFilteredByDcsId<ProductModule>(ContentType.PRODUCT_MODULE, product.id);

  const toggleInfoModalClickHandler = () => {
    setIsModalOpen(prev => !prev);
  };

  const addItemButtonClickHandler = () => {
    addProduct(product);
  };

  const productDetails = info && info.data;

  const dataTestIdProductAddButton = `${stringFormatForTestAttr(product.id)}-add-button`;
  const dataTestIdProductDetail = `${stringFormatForTestAttr(product.id)}-detail`;
  const dataTestIdProductImage = `${stringFormatForTestAttr(product.id)}-image`;
  const dataTestIdProductPrice = `${stringFormatForTestAttr(product.id)}-price`;

  return (
    <div className={styles.accessoryItem}>
      <ProductDetails
        details={productDetails}
        aria-label={productDetails ? t('expand-your-system.button-open-modal-aria-label') : undefined}
        className={styles.accessoryItemProduct}
        onClick={productDetails ? toggleInfoModalClickHandler : undefined}
        data-testid={dataTestIdProductDetail}
      >
        <Image
          src={product.imageURL || naImage}
          className={styles.accessoryItemImage}
          alt={t('expand-your-system.image-alt-text')}
          data-testid={dataTestIdProductImage}
          role="presentation"
        />

        <div className={styles.accessoryItemDetails}>
          <TextV2
            tagName="h3"
            variant="h5"
            value={product.name}
            data-testid={ACCESSORY_ITEM_TEST_ID.productName}
            className={styles.accessoryItemDetailsTitle}
          />
          <TextV2
            tagName="h4"
            variant="body"
            value={accessoryCostByPaymentType}
            data-testid={dataTestIdProductPrice}
          />
        </div>
      </ProductDetails>

      <SproutButton
        variant={LinkVariant.SECONDARY}
        onClick={addItemButtonClickHandler}
        className={styles.accessoryItemButton}
        data-testid={dataTestIdProductAddButton}
        aria-label={t('expand-your-system.add-label')}
      >
        {t('expand-your-system.add')}
      </SproutButton>
      {productDetails && (
        <ProductModal
          closeModal={toggleInfoModalClickHandler}
          modalOpen={isModalOpen}
          productDetails={productDetails}
        />
      )}
    </div>
  );
};

AccessoryItem.displayName = 'AccessoryItem';
